/* eslint-disable @graphql-eslint/no-anonymous-operations */
import { FC, useMemo, useCallback, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

// components
import { Icon } from "../Icon/Icon"

// icons
import { CloseIcon, Typography } from "@eventbrite/marmalade"

// utils
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { events } from "../../utils/heap/tracking_events"
import { extractLangCode, LANG_CODES_TO_TLD } from "../../utils/general_utils"

// styles
import * as styles from "./Banner.module.scss"

// types
type LANG_CODES = keyof typeof LANG_CODES_TO_TLD
type BannerQueryKeys = keyof Omit<
  Queries.BannersQuery["allContentfulBanner"]["group"][0]["nodes"][0],
  "node_locale" | "bannerInternalTitle"
>
type BannerMessage = Pick<
  Queries.ContentfulBannerMessage,
  "callToAction" | "siteBannerMessage" | "siteBannerHeader"
>
type FinalBannerMessage = BannerMessage & { key: string }

const langCodeToBannerMapping: {
  [K in LANG_CODES]: BannerQueryKeys
} = {
  "es-ar": "argentina",
  "en-au": "australia",
  "pt-br": "brazil",
  "en-ca": "canadaEnglish",
  "fr-ca": "canadaFrench",
  "es-cl": "chile",
  "es-co": "columbia",
  da: "denmark",
  fi: "finland",
  fr: "france",
  de: "german",
  "en-hk": "hongKong",
  "en-ie": "ireland",
  it: "italy",
  "es-mx": "mexico",
  "nl-nl": "netherlands",
  "en-nz": "newzealand",
  "es-pe": "peru",
  "pt-pt": "portugal",
  "en-sg": "singapore",
  es: "spain",
  sv: "sweden",
  "fr-ch": "switzerlandFrench",
  "de-ch": "switzerlandGerman",
  "en-gb": "uk",
  "en-us": "usa"
}

export const Banner: FC = () => {
  const contentfulBannerData = useStaticQuery<Queries.BannersQuery>(graphql`
    query Banners {
      allContentfulBanner(
        filter: {
          bannerInternalTitle: {
            ne: "Placeholder Banner (do not delete or unpublish)"
          }
        }
      ) {
        group(field: { contentful_id: SELECT }) {
          nodes {
            node_locale
            bannerInternalTitle
            usa: bannerLocaleUnitedStates {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            uk: bannerLocaleUnitedKingdom {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            german: bannerLocaleDeutschland {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            peru: bannerLocalePeru {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            chile: bannerLocaleChile {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            brazil: bannerLocaleBrasil {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            spain: bannerLocaleEspana {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            france: bannerLocaleFrance {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            italy: bannerLocaleItalia {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            mexico: bannerLocaleMexico {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            sweden: bannerLocaleSweden {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            switzerlandFrench: bannerLocaleSuisse {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            switzerlandGerman: bannerLocaleSchweiz {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            denmark: bannerLocaleDenmark {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            finland: bannerLocaleFinland {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            ireland: bannerLocaleIreland {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            canadaEnglish: bannerLocaleCanadaEn {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            canadaFrench: bannerLocaleCanadaFr {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            columbia: bannerLocaleColombia {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            hongKong: bannerLocaleHongKong {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            portugal: bannerLocalePortugal {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            argentina: bannerLocaleArgentina {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            australia: bannerLocaleAustralia {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            netherlands: bannerLocaleNederland {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            singapore: bannerLocaleSingapore {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
            newzealand: bannerLocaleNewZealand {
              siteBannerMessage {
                raw
              }
              siteBannerHeader
              callToAction {
                raw
              }
            }
          }
        }
      }
    }
  `)

  const langCode = useMemo(() => extractLangCode(), [])

  const filteredBannerData =
    contentfulBannerData?.allContentfulBanner?.group?.length > 0
      ? contentfulBannerData?.allContentfulBanner?.group?.map((x) => {
          return x?.nodes
            .map((node, i) => {
              if (
                node?.node_locale?.toLowerCase() === langCode?.toLowerCase()
              ) {
                return node
              }
            })
            .filter((y) => y)
        })
      : null
  const matchedBannersToLangCode: FinalBannerMessage[][] =
    filteredBannerData?.map((banner) => {
      if (banner?.length > 0) {
        return banner
          .map((singleItem) => {
            const langCodeToBannerKey =
              langCodeToBannerMapping[langCode?.toLowerCase()]

            const matchingSingularBanner: BannerMessage =
              singleItem[langCodeToBannerKey]
            const siteBannerMessage = renderRichText(
              matchingSingularBanner?.siteBannerMessage?.raw
            )

            const key = siteBannerMessage
              ? siteBannerMessage[0].props.children[0]
              : matchingSingularBanner?.siteBannerMessage?.raw?.slice(0, 30)

            const siteBannerHeader = matchingSingularBanner?.siteBannerHeader;

            if (matchingSingularBanner) {
              return {
                callToAction: matchingSingularBanner?.callToAction,
                siteBannerMessage: matchingSingularBanner?.siteBannerMessage,
                siteBannerHeader: siteBannerHeader,
                key: key
              }
            }
          })
          .filter((y) => y)
      }
    })

  if (matchedBannersToLangCode?.length === 0) {
    return null
  }

  const [bannerMessages, setBannerMessages] = useState<FinalBannerMessage[]>(
    matchedBannersToLangCode?.flat()
  )

  const handleCrossIconClick = useCallback(
    (bannerMessageKey: string) => {
      setBannerMessages((bannerMessages) => {
        const filtered = bannerMessages.filter((x) => {
          if (x && x.key === bannerMessageKey) {
            return false
          }

          return true
        })

        return filtered
      })
    },
    [filteredBannerData, bannerMessages]
  )

  return (
    <div className={styles.wrapper}>
      {bannerMessages?.length > 0
        ? bannerMessages.map((banner) => {
            if (
              !banner?.key ||
              banner?.key?.trim()?.toLowerCase() === "placeholder message"
            ) {
              return null
            }

            const callToAction = renderRichText(banner?.callToAction?.raw)
            const siteBannerMessage = renderRichText(
              banner?.siteBannerMessage?.raw,
              customRendererToRemovePTag
            )
          const siteBannerHeader = banner?.siteBannerHeader;

            const callToActionUrl =
              banner?.callToAction?.raw?.length > 0
                ? JSON.parse(banner?.callToAction?.raw)?.content?.find(
                    (x) => x.nodeType === "hyperlink"
                  )?.data?.uri
                : ""

            return (
              <SingleBannerMessage
                key={banner.key}
                bannerMessageKey={banner.key}
                handleCrossIconClick={handleCrossIconClick}
                callToAction={callToAction}
                siteBannerMessage={siteBannerMessage}
                siteBannerHeader={siteBannerHeader}
                callToActionUrl={callToActionUrl}
              />
            )
          })
        : null}
    </div>
  )
}

type SingleBannerMessageProps = {
  handleCrossIconClick: (siteBannerMessage: string) => void
  callToAction: React.ReactNode
  siteBannerMessage: React.ReactNode
  bannerMessageKey: string
  callToActionUrl: string
  siteBannerHeader?: string | React.ReactNode
}

const SingleBannerMessage: FC<SingleBannerMessageProps> = ({
  callToAction,
  siteBannerMessage,
  bannerMessageKey,
  handleCrossIconClick,
  callToActionUrl,
  siteBannerHeader
}) => {
  const handleClick = useCallback(() => {
    handleCrossIconClick(bannerMessageKey)
  }, [callToAction, siteBannerMessage, handleCrossIconClick])

  return (
    <div className={styles.container}>
      <div className={styles.messageContainer}>
        {siteBannerHeader && (
          <div className={styles.titleContainer}>
            <Typography variant="heading-xs"  color="Neutral 600" className={styles.title}>
              {siteBannerHeader}
            </Typography>
          </div>
        )}
        {siteBannerMessage && (
          <Typography variant="heading-xs">{siteBannerMessage}</Typography>
        )}
      </div>

      <div className={styles.callToActionContainer}>
        <span
          onClick={(e) => {
            events?.bannerClick(callToActionUrl, SingleBannerMessage?.name)
          }}
        >
          {callToAction}
        </span>
      </div>
      
      <Icon
        iconProps={{
          onClick: handleClick
        }}
        className={styles.crossIcon}
        icon={<CloseIcon size="small" />}
      />
    </div>
  )
}

export default Banner